import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CellContext,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { CommitmentMechanicsFormData, CommitmentProduct } from './types';
import { colors } from 'packages/common-frontend/colors';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { remToPx } from 'packages/common-frontend/theme/utils';
import { AddProductDrawer } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/AddProductDrawer';
import { useProductDiffResult } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/hooks';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import {
  DiffTag,
  ShowWhileDiffing,
  ShowWhileEditing,
} from '~/core/routes/settings/implementations/common/DiffInputs';

// Define the meta type for tanstack table
// The tanstack docs show a different way to type this property, but it is global
// to all tables. So we will just do it this way instead. See: https://tanstack.com/table/v8/docs/api/core/table#meta
type ProductTableMeta = {
  remove: (index: number) => void;
};

const RenderMenu = ({ row, table }: CellContext<CommitmentProduct, any>) => {
  const menuControls = useDisclosure();
  const { showConfirmDeleteModal } = useConfirmDeleteContext();

  // Access the meta property with proper typing
  const meta = table.options.meta as ProductTableMeta;

  return (
    <ShowWhileEditing>
      <Flex
        justify="right"
        // stopPropagation() here prevents the onClick handler on table rows from being triggered.
        // See the 'onRowClick' handler on the TableBETA component
        onClick={(e) => e.stopPropagation()}
        overflow="hidden"
      >
        <Menu {...menuControls} computePositionOnMount strategy="fixed">
          <MenuButton
            as={IconButtonV2}
            variant="secondary-muted"
            colorScheme="gray"
            isActive={menuControls.isOpen}
            aria-label="Facility options"
            icon={<Icon icon="ellipsis-vertical" />}
            tooltipEnabled={false}
          />
          <MenuList zIndex="dropdown">
            <MenuItem
              color={colors.gray[800]}
              onClick={() =>
                showConfirmDeleteModal(row.original.name, () => {
                  meta.remove(row.index);
                })
              }
              // Must have at least one product, because that's where in the
              // credit agreement we store per-lender commitment amount.
              isDisabled={table.getRowCount() <= 1}
            >
              Delete Product
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </ShowWhileEditing>
  );
};

const RenderProductName = ({
  row,
  getValue,
}: CellContext<CommitmentProduct, 'name'>) => {
  const diffResult = useProductDiffResult(row.original.pricingOptionKey);

  return (
    <Flex gap="1rem" alignItems="baseline">
      <ShowWhileDiffing>
        <DiffTag diff={diffResult?.diffResult} disableTooltip />
      </ShowWhileDiffing>
      {getValue()}
    </Flex>
  );
};
const columnHelper = createColumnHelper<CommitmentProduct>();

const columnDefs = [
  columnHelper.accessor('name', {
    header: 'Product Name',
    enableSorting: false,
    cell: RenderProductName,
  }),
  columnHelper.accessor('productType', {
    header: 'Product Type',
    enableSorting: false,
    cell: function RenderProductType({ getValue }) {
      return <Box textTransform="capitalize">{getValue()}</Box>;
    },
  }),
  columnHelper.accessor('pricingOptionKey', {
    header: 'Pricing Option',
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'rowMenu',
    header: '',
    size: remToPx(1),
    enableSorting: false,
    cell: RenderMenu,
  }),
];

export const ProductsTable = ({
  formKey,
}: {
  formKey: 'wipForm' | 'diffForm';
}) => {
  const { append, remove, update, fields } = useFieldArray<
    CommitmentMechanicsFormData,
    'wipForm.products' | 'diffForm.products'
  >({
    name: `${formKey}.products`,
  });

  const form = useFormContext<CommitmentMechanicsFormData>();

  const commitmentKey = useWatch<CommitmentMechanicsFormData, 'wipForm.key'>({
    name: 'wipForm.key',
  });

  const [productToDisplay, setProductToDisplay] = useState<CommitmentProduct>();
  const [editIndex, setEditIndex] = useState<number | undefined>();

  const disclosure = useDisclosure();

  const handleSetProductToDisplay = (
    product: CommitmentProduct,
    index: number,
  ) => {
    const formValues = form.getValues();
    const wipProduct = formValues.wipForm.products.find(
      (p) => p.pricingOptionKey === product.pricingOptionKey,
    );
    const diffProduct = formValues.diffForm?.products.find(
      (p) => p.pricingOptionKey === product.pricingOptionKey,
    );
    setProductToDisplay(wipProduct || diffProduct);
    setEditIndex(index);
  };

  const meta: ProductTableMeta = {
    remove,
  };

  const table = useReactTable({
    data: fields,
    columns: columnDefs,
    meta,
    getCoreRowModel: getCoreRowModel(),
  });

  // Handler for when the drawer form is submitted
  const handleAddOrUpdateProduct = (data: CommitmentProduct) => {
    if (editIndex !== undefined) {
      update(editIndex, data);
    } else {
      append(data);
    }
  };

  return (
    <Flex direction="column" gap="1rem">
      <Flex justifyContent="space-between" direction="column" gap="1rem">
        <Flex alignItems="center" justifyContent="space-between">
          <Typography variant="section-title" trim>
            Products
          </Typography>
          <ShowWhileEditing>
            <Button
              colorScheme="v2-aurora"
              variant="secondary"
              leftIcon={<Icon icon="plus" />}
              onClick={() => {
                setProductToDisplay(undefined);
                setEditIndex(undefined);
                disclosure.onOpen();
              }}
            >
              Add Product
            </Button>
          </ShowWhileEditing>
        </Flex>
        <Typography variant="base-muted">
          At least one product per commitment is required, and all listed
          products share the total commitment amount specified above—individual
          products do not have separate commitments.
        </Typography>
        <AddProductDrawer
          disclosure={disclosure}
          commitmentKey={commitmentKey}
          onAddProduct={handleAddOrUpdateProduct}
          existingProduct={productToDisplay}
        />
      </Flex>
      {table.getRowCount() > 0 && (
        <TableBETA
          table={table}
          onRowClick={(row) => {
            handleSetProductToDisplay(row.original, row.index);
            disclosure.onOpen();
          }}
        />
      )}
    </Flex>
  );
};
